/**
 * Reading the family's records, a handful of rows at a time.
 *
 * This is the answer to "understand the data without being heavy". Sending the
 * whole family with every question would be slow, expensive and almost always
 * wasted — most questions are about buttons, not people. So nothing is sent up
 * front, and when a question genuinely needs the records the model asks for
 * exactly what it needs and gets back a few short lines.
 *
 * Three rules keep it light:
 *
 *  - **A cap, always.** Never more than a dozen rows, whatever was asked.
 *  - **One line each.** A person is a name, a lifespan and where they live —
 *    not a record with sixteen fields, most of which answer nothing.
 *  - **Ids come back.** So the model can say `open person <id>` without ever
 *    having to guess at one, and cannot invent a person who does not exist.
 *
 * It reads what the signed-in account can already see: `stories` is the list the
 * app itself filtered for them, and archived or merged people are left out the
 * same way the tree leaves them out.
 */
import { approxYear, currentAge, formatFuzzyDate, lifeYears } from '../../domain/dates';
import { displayName, searchTerms, normalize } from '../../domain/names';
import { locationLabel, whereIsAt } from '../../domain/places';
import type { Occasion } from '../../domain/occasions';
import { occasionDayLabel } from '../../domain/occasions';
import { kinLabel, kinshipKey, type FamilyGraph, type KinKey } from '../../domain/relationships';
import type { FamilyPhoto, Gender, Lang, Location, PersonPlace, PersonWithNames, Story, UUID } from '../../domain/types';

const MAX = 12;

export interface LookupSource {
  people: PersonWithNames[];
  stories: Story[];
  moments: FamilyPhoto[];
  places: PersonPlace[];
  locationsById: Map<UUID, Location>;
  occasions: Occasion[];
  today: string;
  me: PersonWithNames | null;
  /** Who is whose parent, partner and child — the tree's own graph, already built. */
  graph: FamilyGraph;
  lang: Lang;
}

function personLine(p: PersonWithNames, src: LookupSource): string {
  const bits: string[] = [`${p.id} · ${displayName(p, 'en')}`];
  const he = displayName(p, 'he');
  if (he && he !== displayName(p, 'en')) bits.push(he);
  const years = lifeYears(p.birth, p.death, p.status === 'deceased', 'en');
  if (years) bits.push(years);
  /*
   * The age, spelled out, rather than left as arithmetic on a birth year.
   *
   * "Who is about my age" and "is he older than me" are ordinary questions and
   * they were being answered badly, because working an age out of "1954" means
   * knowing what year it is now — which a model reading a list of rows does
   * not reliably know, and gets confidently wrong when it guesses.
   */
  const age = currentAge(p.birth, p.death, p.status === 'deceased');
  if (age !== null && age >= 0 && age < 130) bits.push(p.status === 'deceased' ? `${age} at death` : `age ${age}`);
  const where = whereIsAt(p, src.places, src.today);
  if (where) {
    const label = locationLabel(src.locationsById.get(where.locationId), 'en');
    if (label) bits.push(where.kind === 'visit' ? `visiting ${label}` : label);
  }
  if (p.reviewStatus === 'pending') bits.push('not yet recognised by the family');
  return bits.join(' · ');
}

function matches(p: PersonWithNames, q: string): boolean {
  const needle = normalize(q);
  if (!needle) return true;
  return searchTerms(p).some((term) => normalize(term).includes(needle));
}

/** Only people the tree itself shows. */
function livePeople(src: LookupSource): PersonWithNames[] {
  return src.people.filter((p) => !p.archived && !p.mergedIntoId);
}

/** Roughly a generation: the bracket "around my age" means when nobody says otherwise. */
export const DEFAULT_SPAN = 12;

/**
 * Everyone born within `span` years of the person asking, closest first.
 *
 * Shared by the `lookup` tool and by the built-in provider, which has no model
 * behind it and has to answer this one itself — so the arithmetic lives in one
 * place and the two cannot disagree about who counts.
 *
 * **Birth year, not age.** Comparing a living grandchild's age with a
 * great-grandfather's age at death would put the two in the same bracket, which
 * is exactly wrong: born forty years apart is what "not my age" means, whoever
 * is still here.
 */
export function bornNear(src: LookupSource, span = DEFAULT_SPAN): { why?: 'noMe' | 'noYear'; year?: number; people: PersonWithNames[] } {
  if (!src.me) return { why: 'noMe', people: [] };
  const mine = approxYear(src.me.birth);
  if (mine === null) return { why: 'noYear', people: [] };
  const people = livePeople(src)
    .filter((p) => p.id !== src.me?.id)
    .map((p) => ({ p, born: approxYear(p.birth) }))
    .filter((x): x is { p: PersonWithNames; born: number } => x.born !== null && Math.abs(x.born - mine) <= span)
    .sort((a, b) => Math.abs(a.born - mine) - Math.abs(b.born - mine))
    .map((x) => x.p);
  return { year: mine, people };
}

export function runLookup(what: string, query: string | undefined, src: LookupSource): string {
  const q = (query ?? '').trim();

  if (what === 'me') {
    if (!src.me) return 'This account is signed in but is not joined to a circle in the tree yet.';
    return `They are: ${personLine(src.me, src)}`;
  }

  if (what === 'people') {
    const hits = livePeople(src).filter((p) => matches(p, q));
    if (!hits.length) return q ? `Nobody in the family matches "${q}".` : 'No people yet.';
    const shown = hits.slice(0, MAX).map((p) => personLine(p, src));
    return [`${hits.length} match${hits.length === 1 ? '' : 'es'}${hits.length > MAX ? `, first ${MAX}` : ''}:`, ...shown].join('\n');
  }

  /*
   * "Who is around my age?"
   *
   * A question people actually ask, and one that used to fall through to a
   * name search for the word "age" and come back with nothing — or worse, with
   * an explanation of something else entirely. It cannot be answered by
   * matching text at all, so it gets its own branch.
   *
   * The comparison is **birth year**, not age today. Comparing a living
   * grandchild's age with a great-grandfather's age at death would put the two
   * of them in the same bracket, which is exactly wrong: born forty years apart
   * is what "not my age" means, whoever is still here.
   */
  if (what === 'sameAge') {
    // A generation is the natural bracket, and a number in `query` overrides it.
    const asked = Number.parseInt(q, 10);
    const span = Number.isFinite(asked) && asked > 0 && asked <= 60 ? asked : DEFAULT_SPAN;
    const near = bornNear(src, span);
    if (near.why === 'noMe') return 'This account is not joined to anybody in the tree yet, so there is nobody to compare with. Ask them to open their profile and join their circle first.';
    if (near.why === 'noYear') return `${displayName(src.me as PersonWithNames, 'en')} has no year of birth recorded, so there is nothing to compare. It can be added on their profile.`;
    if (!near.people.length) return `Nobody else in the family was born within ${span} years of ${near.year}. Widen the range, or many people may have no year of birth recorded yet.`;
    const shown = near.people.slice(0, MAX).map((p) => personLine(p, src));
    const head = `Born within ${span} years of ${displayName(src.me as PersonWithNames, 'en')} (${near.year})`;
    return [`${head}${near.people.length > MAX ? `, closest ${MAX} of ${near.people.length}` : ''}:`, ...shown].join('\n');
  }

  /*
   * "Where is my father?" — and every other question with a *relationship* in it
   * rather than a name.
   *
   * This branch exists because of one failure worth remembering. Somebody asked
   * where their father was, and the assistant searched the family for the word
   * "father", found nobody called that, and answered around it. It had no way to
   * know that "my father" names a person: every other lookup here matches text
   * against names, and a relationship is not written down anywhere as text.
   *
   * So the tree's own graph answers it — the same graph the tree draws itself
   * from, so the assistant cannot believe something the screen disagrees with.
   * Each row is labelled with *how* they are related ("Your father", "Your
   * sister"), which is the half the model needs, and carries the id, which is
   * what `show` needs to put them on screen.
   *
   * `query` narrows by relationship word rather than by name — "father",
   * "grandparents", "brother" — because that is what the question contained.
   */
  if (what === 'relatives') {
    if (!src.me) return 'This account is not joined to anybody in the tree yet, so there are no relationships to read. Their profile can join them to their circle.';
    const meId = src.me.id;
    const genderOf = (id: UUID): Gender => src.people.find((p) => p.id === id)?.gender ?? 'unspecified';
    const needle = normalize(q);
    const rows = livePeople(src)
      .filter((p) => p.id !== meId)
      .map((p) => ({ p, key: kinshipKey(src.graph, meId, p.id, genderOf) }))
      // Anybody the graph cannot place is left out rather than listed as "your
      // relative": a list padded with people whose connection is unknown is how
      // a model ends up confidently naming the wrong person.
      .filter((x): x is { p: PersonWithNames; key: KinKey } => !!x.key && x.key !== 'relative')
      .map((x) => ({ ...x, label: kinLabel(x.key, 'en') }))
      .filter((x) => !needle || normalize(x.label).includes(needle) || normalize(x.key).includes(needle) || matches(x.p, q));
    if (!rows.length) {
      return q
        ? `Nobody in the tree is ${q} to them — either that relationship is not recorded, or the word does not match one. Try lookup relatives with no query to see everyone they are connected to.`
        : 'Nobody is connected to them in the tree yet.';
    }
    // Closest first: parents and children before cousins and in-laws, so a cap
    // of a dozen rows never cuts off the person actually being asked about.
    const rank = (k: string) =>
      /^(mother|father|parent|son|daughter|child|wife|husband|partner)/.test(k) ? 0
        : /^(sister|brother|sibling|half_)/.test(k) ? 1
          : /^(grand)/.test(k) ? 2
            : /^(great_)/.test(k) ? 3 : 4;
    rows.sort((a, b) => rank(a.key) - rank(b.key));
    const shown = rows.slice(0, MAX).map((x) => `${x.label}: ${personLine(x.p, src)}`);
    return [
      `${rows.length} relative${rows.length === 1 ? '' : 's'}${rows.length > MAX ? `, closest ${MAX}` : ''}. The id is the first field — pass it to show to put them on screen:`,
      ...shown,
    ].join('\n');
  }

  if (what === 'stories') {
    const needle = normalize(q);
    const hits = src.stories.filter((s) => !needle || normalize(s.title).includes(needle));
    if (!hits.length) return q ? `No story matches "${q}".` : 'No stories yet.';
    const byNewest = [...hits].sort((a, b) => (b.publishedAt ?? b.createdAt).localeCompare(a.publishedAt ?? a.createdAt));
    const shown = byNewest.slice(0, MAX).map((s) => {
      const teller = s.primaryStorytellerPersonId ? src.people.find((p) => p.id === s.primaryStorytellerPersonId) : undefined;
      return `${s.id} · "${s.title}"${teller ? ` · told by ${displayName(teller, 'en')}` : ''}${s.date ? ` · ${formatFuzzyDate(s.date, 'en', { short: true })}` : ''}`;
    });
    return [`${hits.length} stor${hits.length === 1 ? 'y' : 'ies'}${hits.length > MAX ? `, newest ${MAX}` : ''}:`, ...shown].join('\n');
  }

  if (what === 'occasions') {
    if (!src.occasions.length) return 'Nothing coming up in the next 30 days.';
    // With ids, like every other row about a person: `show` needs one to turn the calendar to their day.
    const nameOf = (ids: UUID[]) => ids.map((id) => src.people.find((p) => p.id === id)).filter(Boolean).map((p) => `${displayName(p as PersonWithNames, 'en')} (${(p as PersonWithNames).id})`).join(' & ');
    const shown = src.occasions.slice(0, MAX).map((o) => {
      const when = occasionDayLabel(o, 'en', { today: 'today', tomorrow: 'tomorrow' });
      const who = nameOf(o.personIds);
      const years = o.years ? ` · ${o.years}` : '';
      return `${o.kind}${who ? ` · ${who}` : ''} · ${when}${years}`;
    });
    return [`Coming up (${src.occasions.length} in the next 30 days):`, ...shown].join('\n');
  }

  if (what === 'places') {
    const seen = new Map<string, string[]>();
    for (const p of livePeople(src)) {
      const where = whereIsAt(p, src.places, src.today);
      if (!where) continue;
      const label = locationLabel(src.locationsById.get(where.locationId), 'en') || 'somewhere';
      const list = seen.get(label) ?? [];
      list.push(displayName(p, 'en'));
      seen.set(label, list);
    }
    if (!seen.size) return 'Nobody has a place recorded yet.';
    return [...seen.entries()].slice(0, MAX).map(([place, who]) => `${place}: ${who.slice(0, 6).join(', ')}${who.length > 6 ? ` and ${who.length - 6} more` : ''}`).join('\n');
  }

  return `I do not know how to look up "${what}".`;
}
